<template>
  <listing-layout>
    <h1 slot="heading" class="sr-only">Rookie Nominations</h1>
    <div slot="content">
      <h2 slot="heading" class="mb-4">Select Horses To Nominate For Rookie Status{{ (loggedInRole === 'Admin' || loggedInRole === 'SuperAdmin') && this.member ? (' for ' + this.member.name) : '' }}</h2>
      <div class="alert alert-warning" v-if="error">{{error}}</div>
      <div class="form-group">
        <label for="financialYear">Paying for Season</label>
        <div class="d-flex">
          <select class="form-control" id="financialYear" v-model="selectedSeason" @change="setSelectedSeason($event)">
            <option v-for="(startYear) in seasonsList()" :key="startYear.year" :value="startYear.year">{{startYear.description}}</option>
          </select>
        </div>
      </div>
      <modal v-if="showSeasonConfirmModal"  @close.prevent="showSeasonConfirmModal = false">
        <h3 slot="header" class="modal-title">Warning</h3>
        <div slot="body" class="text-center">
          <p>{{ this.warningText }}</p>
        </div>
        <div slot="footer">
          <button class="btn btn-outline-primary" @click.prevent="showSeasonConfirmModal = false">No</button>
          <button class="btn btn-primary" @click="checkout">Yes</button>
        </div>
      </modal>
      <data-table :dataRows="rows" :dataColumns="columns" :options="options">
        <tableSelectRowTemplate slot-scope="{ row }" slot="expandedSelect" :row="row" class="z-1 p-3" />
        <tableTextTemplate slot-scope="{ row }" slot="expandedHeader" :text="row.name" :row="row" :options="{ expandMobile: true }" />
        <div slot-scope="{ row }" slot="expandedRow">
          <ul class="list-unstyled mb-0 pt-2">
            <li>
              <strong>Owner: </strong>
              <tableTextTemplate :text="row.owner_name" :row="row" :options="{ showRegistered: true, inline: true }" />
            </li>
            <li>
              <strong>Age: </strong>
              <tableTextTemplate :text="String(row.age)" :row="row" :options="{ inline: true }" />
            </li>
            <li>
              <strong>Sex: </strong>
              <tableTextTemplate :text="row.gender" :row="row" :options="{ inline: true }" />
            </li>
            <li>
              <strong>Status: </strong>
              <tableTextTemplate :text="$store.state.horseStatus.some(status => status.id === row.horse_status_id) ? $store.state.horseStatus.filter(status => status.id === row.horse_status_id)[0].name : ''" :row="row" :options="{ inline: true }" />
            </li>
          </ul>
        </div>
      </data-table>
      <div class="sticky-bottom alert bg-primary text-white d-flex flex-wrap align-items-center mb-0" role="alert" v-if="$store.getters.selectedRows.length > 0">
        <div class="flex-grow-1 pr-3 text-center text-lg-left">
          <strong>You have selected: </strong>
          <span v-for="(row, i) in $store.getters.selectedRows" :key="i">
            {{ row.hasOwnProperty('first_name') ? (row.first_name + ' ' + row.last_name) : row.name + (i == $store.getters.selectedRows.length -1 ? '' : ',') }}
          </span>
        </div>
        <button :class="['btn  btn-block-mob mt-2 mt-lg-0 text-nowrap', processing ? 'btn-primary-dark' : 'btn-outline-white']" type="submit" @click="checkSelectedSeason()">
          {{ !processing ? 'Nominate For Rookie Status' : '' }}
          <div v-if="processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
        </button>
        <button v-if="options.joinButtonText || options.registerHorseText" class="btn btn-outline-white ml-lg-2 btn-block-mob" @click="deselectAll">Cancel</button>
      </div>
    </div>
  </listing-layout>
</template>

<script>
/* Use the "slot" attribute to match the heading and content
 * to their respective fields in the ListingLayout component. */

import axios from 'axios'
import listingLayout from '@/components/ListingLayout'
import dataTable from '@/components/DataTable'
import tableTextTemplate from '@/components/DataTableFields/Text'
import tableSelectRowTemplate from '@/components/DataTableFields/SelectRow'
import modal from '@/components/Modal'

export default {
  name: 'NominateRookie',
  components: {
    'listing-layout': listingLayout,
    'data-table': dataTable,
    tableTextTemplate,
    tableSelectRowTemplate,
    modal
  },
  props: {
    member: {
      type: Object
    }
  },
  data() {
    return {
      error: '',
      rows: [],
      warningText: '',
      columns: [
        {
          label: '',
          field: '',
          headerClass: 'column-lg-small d-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableSelectRowTemplate },
            template: `<tableSelectRowTemplate :row="row" />`
          })
        },
        {
          label: 'Horse Name',
          headerClass: 'd-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.name" :row="row" :options="{ showRegistered: true }" />`
          }),
          representedAs(row) { return `${row.name}` }
        },
        {
          label: 'Owner',
          headerClass: 'd-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.owner_name" :row="row" />`
          }),
          representedAs(row) { return `${row.owner_name}` }
        },
        {
          label: 'Age',
          headerClass: 'd-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="String(row.age)" :row="row" />`
          }),
          representedAs(row) { return `${row.age}` }
        },
        {
          label: 'Sex',
          headerClass: 'd-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.gender" :row="row" />`
          }),
          representedAs(row) { return `${row.gender}` }
        },
        {
          label: 'Status',
          field: 'horse_status_id',
          headerClass: 'd-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="rowStatus(row.horse_status_id)" :row="row" />`,
            methods: {
              rowStatus(theID) {
                return this.$store.state.horseStatus.some(status => status.id === theID) ? this.$store.state.horseStatus.find(status => status.id === theID).name : ''
              }
            }
          }),
          representedAs(row) { return `${row.horse_status_name}` }
        }
      ],
      options: {
        selectRows: true,
        registerHorseText: true,
        loaded: false,
        activeFilter: {
          // Has to be registered before you can nominate
          registration_date: true
        },
        search: true
      },
      processing: false,
      loggedInRole: localStorage.role,
      rookieYears: [],
      selectedSeason: false,
      startYear: 0,
      saveRows: [],
      showSeasonConfirmModal: false
    }
  },
  computed: {
    horseStatus() {
      return this.$store.state.horseStatus
    }
  },
  watch: {
    horseStatus() {
      // Match horse status id to horse status name
      if (this.horseStatus.length) {
        this.rows = this.rows.map((item) => {
          const tempItem = item
          tempItem.horse_status_name = this.horseStatus.some(status => status.id === tempItem.horse_status_id) ? this.horseStatus.find(status => status.id === tempItem.horse_status_id).name : ''
          return tempItem
        })
      }
    }
  },
  mounted() {
    if ((this.loggedInRole === 'Admin' || this.loggedInRole === 'SuperAdmin') && !this.member) {
      this.$router.push('/')
    }
    this.$store.commit('deselectAllRows')
    axios.post(`${this.$api}/horse/my-horses`, {
      id: (this.loggedInRole === 'Admin' || this.loggedInRole === 'SuperAdmin') ? this.member.id : localStorage.userID,
      ownedOnly: true
    }).then((response) => {
      if (response.data.status === 'success') {
        this.options.loaded = true
        if (response.data.data.length === 0) {
          this.status = 'You have no horses in your list. Press add to add your first horse.'
        }
        let tempRows = response.data.data
        // Match horse status id to horse status name
        if (this.horseStatus.length) {
          tempRows = response.data.data.map((item) => {
            const tempItem = item
            tempItem.horse_status_name = this.horseStatus.some(status => status.id === tempItem.horse_status_id) ? this.horseStatus.find(status => status.id === tempItem.horse_status_id).name : ''
            return tempItem
          })
        }
        // We only want horses that aren't already rookies
        this.rows = tempRows.filter(item => !item.is_rookie)
        this.saveRows = [...tempRows]
      }
    }).catch((error) => {
      this.options.loaded = true
      // eslint-disable-next-line
      console.log(error)
    })

    axios.get(`${this.$api}/horse/rookie-years`)
      .then((response) => {
        this.rookieYears = response.data
        this.startYear = response.data[0]
        this.selectedSeason = response.data[0]
      })
  },
  methods: {
    deselectAll() {
      this.$store.commit('deselectAllRows')
    },
    checkout() {
      this.processing = true
      const products = this.$store.getters.selectedRows
      // Products data may not have loaded yet - gotta wait for that
      if (this.$store.state.products.length) {
        this.goToCart(products)
      } else {
        this.waitForProducts(products)
      }
    },
    goToCart(products) {
      // Populate Array of length equalling the number of selected horses with the id of the rookie nomination product
      const productIds = Array(products.length).fill(this.$store.state.products.find(product => product.sku === `rookie-nomination-${this.selectedSeason}`).id)
      axios.post(`${this.$api}/total-cart`, { data: productIds }).then((response) => {
        this.processing = false
        if (response.data.status === 'success') {
          const horseData = response.data
          horseData.horses = products
          this.$store.commit('updateCartMembers', horseData)
          this.$router.push({ name: 'Nominate Rookie Cart', params: { nominate: 'rookie' } })
        }
      })
    },
    waitForProducts(products) {
      setTimeout(() => {
        if (this.$store.state.products.length) {
          this.goToCart(products)
        } else {
          this.waitForProducts(products)
        }
      }, 10)
    },
    seasonsList() {
      let years = [] // eslint-disable-line prefer-const

      this.rookieYears.forEach((year) => {
        if (year) {
          const description = `April 1, ${year} to March 31, ${year + 1}`
          years.push({ year, description })
        }
      })

      return years
    },
    setSelectedSeason(event) {
      this.selectedSeason = event.target.value
      this.rows = this.saveRows
      this.rows = this.rows.filter(item => !item.is_rookie)
    },
    checkSelectedSeason() {
      if (String(this.startYear) !== String(this.selectedSeason)) {
        this.warningText = "Rookie horse season doesn't start until 1st April - Horses must not have started in an open age event to be eligible. Proceed?"
        this.showSeasonConfirmModal = true
      } else {
        this.showSeasonConfirmModal = false
        this.checkout()
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/Horses/Nominate/NominateRookie.vue